/**
 * AcquireOS AI - LoopNet RapidAPI market data service.
 *
 * Primary search endpoint (verified working July 2026):
 *   POST /loopnet/v2/sale/searchByAddress
 *     body: {"country":"US","state":...,"city":...,"county":null,"zipCode":null,"page":1}
 *     response: { data: [ { allListingIds: [...], listings: [...] } ] }
 *
 * All v1 search endpoints (searchByCity, searchByState, searchByBoundingBox,
 * searchByCoordination, searchByZipCode) currently return
 * 400 {"message":"Request failed. Please try again."} from the provider's
 * upstream, so they are used only as last-resort fallbacks.
 *
 * Details are hydrated via POST /loopnet/property/bulkDetails as before.
 * Market stats are derived from listing search + property detail responses.
 */

// Direct mode (VITE_LOOPNET_DIRECT=true) calls RapidAPI from the browser and
// requires VITE_LOOPNET_API_KEY. Default is proxy mode: calls go to the
// FastAPI backend at /api/loopnet/*, which holds the key server-side and
// caches responses across all visitors (see backend/app/loopnet.py).
const DIRECT_MODE = String(import.meta.env.VITE_LOOPNET_DIRECT || '').toLowerCase() === 'true'
const LOOPNET_KEY = (import.meta.env.VITE_LOOPNET_API_KEY || '').trim()
const LOOPNET_HOST = (import.meta.env.VITE_LOOPNET_RAPIDAPI_HOST || 'loopnet-api.p.rapidapi.com').trim()
const LOOPNET_BASE = DIRECT_MODE ? `https://${LOOPNET_HOST}` : '/api'
const SALE_DETAILS_PATH = (import.meta.env.VITE_LOOPNET_SALE_DETAILS_PATH || '/loopnet/property/SaleDetails').trim()
const LEASE_DETAILS_PATH = (import.meta.env.VITE_LOOPNET_LEASE_DETAILS_PATH || '/loopnet/property/LeaseDetails').trim()
const BULK_DETAILS_PATH = (import.meta.env.VITE_LOOPNET_BULK_DETAILS_PATH || '/loopnet/property/bulkDetails').trim()
const DETAIL_BODY_KEY = (import.meta.env.VITE_LOOPNET_DETAIL_BODY_KEY || 'listingId').trim()
const DEFAULT_SEARCH_LIMIT = Number(import.meta.env.VITE_LOOPNET_SEARCH_LIMIT || 500)
const BULK_DETAILS_BATCH_SIZE = Number(import.meta.env.VITE_LOOPNET_BULK_BATCH_SIZE || 20)

const CACHE_TTL_MS = 5 * 60 * 1000
const PERSISTED_CACHE_TTL_MS = 60 * 60 * 1000
const PERSISTED_PREFIX = 'loopnet-cache:'
const CACHE = new Map()
const ERROR_CACHE = new Map()
const IN_FLIGHT = new Map()

const STATE_NAMES = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California',
  CO: 'Colorado', CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia',
  HI: 'Hawaii', ID: 'Idaho', IL: 'Illinois', IN: 'Indiana', IA: 'Iowa',
  KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana', ME: 'Maine', MD: 'Maryland',
  MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota', MS: 'Mississippi', MO: 'Missouri',
  MT: 'Montana', NE: 'Nebraska', NV: 'Nevada', NH: 'New Hampshire', NJ: 'New Jersey',
  NM: 'New Mexico', NY: 'New York', NC: 'North Carolina', ND: 'North Dakota', OH: 'Ohio',
  OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania', RI: 'Rhode Island', SC: 'South Carolina',
  SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas', UT: 'Utah', VT: 'Vermont',
  VA: 'Virginia', WA: 'Washington', WV: 'West Virginia', WI: 'Wisconsin', WY: 'Wyoming',
  DC: 'District of Columbia',
}

const FIND_STATE_PATH = (import.meta.env.VITE_LOOPNET_FIND_STATE_PATH || '/loopnet/helper/findState').trim()
const STATE_BODY_SHAPE_KEY = 'loopnet-state-body-shape'

const STATE_DEFAULT_CITY = {
  TX: 'Austin',
  FL: 'Miami',
  AZ: 'Phoenix',
  CA: 'Los Angeles',
  NY: 'New York',
  IL: 'Chicago',
  GA: 'Atlanta',
  CO: 'Denver',
  NC: 'Charlotte',
  TN: 'Nashville',
  WA: 'Seattle',
  MA: 'Boston',
  PA: 'Philadelphia',
  OH: 'Columbus',
}

export const _apiState = {
  lastSearchError: null,
  lastSearchOk: false,
  lastMarketError: null,
  lastMarketOk: false,
  lastStatus: 'idle',
  lastMessage: null,
}

export function getLoopNetApiState() {
  return { ..._apiState }
}

function setApiState({ type = 'search', ok, error = null, status = ok ? 'live' : 'error', message = null }) {
  const errorKey = type === 'market' ? 'lastMarketError' : 'lastSearchError'
  const okKey = type === 'market' ? 'lastMarketOk' : 'lastSearchOk'
  _apiState[errorKey] = error
  _apiState[okKey] = ok
  _apiState.lastStatus = status
  _apiState.lastMessage = message || error?.message || null
}

function cacheGet(store, key) {
  const entry = store.get(key)
  if (!entry) return null
  if (Date.now() - entry.ts > CACHE_TTL_MS) {
    store.delete(key)
    return null
  }
  return entry.value
}

function cacheSet(store, key, value) {
  store.set(key, { ts: Date.now(), value })
  return value
}

function persistedGet(key) {
  try {
    const raw = localStorage.getItem(`${PERSISTED_PREFIX}${key}`)
    if (!raw) return null
    const entry = JSON.parse(raw)
    if (Date.now() - entry.ts > PERSISTED_CACHE_TTL_MS) {
      localStorage.removeItem(`${PERSISTED_PREFIX}${key}`)
      return null
    }
    return entry.value
  } catch {
    return null
  }
}

function persistedSet(key, value) {
  try {
    localStorage.setItem(`${PERSISTED_PREFIX}${key}`, JSON.stringify({ ts: Date.now(), value }))
  } catch {
    // Local cache is best-effort only.
  }
  return value
}

async function dedupe(key, fn) {
  if (IN_FLIGHT.has(key)) return IN_FLIGHT.get(key)
  const promise = fn().finally(() => IN_FLIGHT.delete(key))
  IN_FLIGHT.set(key, promise)
  return promise
}

function loopnetHeaders() {
  if (!DIRECT_MODE) return { 'Content-Type': 'application/json' }
  return {
    'Content-Type': 'application/json',
    'X-RapidAPI-Key': LOOPNET_KEY,
    'X-RapidAPI-Host': LOOPNET_HOST,
  }
}

function httpErrorMessage(status, body = '') {
  const lowerBody = String(body || '').toLowerCase()
  if (status === 401) return '401 Unauthorized - API key is missing or invalid'
  if (status === 403) return '403 Forbidden - API key does not have access to this endpoint'
  if (status === 404) return '404 Not Found - LoopNet endpoint URL was not found'
  if (status === 429 && lowerBody.includes('monthly quota')) {
    return 'LoopNet API monthly quota exceeded (429) - RapidAPI BASIC plan has no fresh requests left. Showing cached or placeholder data until the plan resets or is upgraded'
  }
  if (status === 429) return 'LoopNet API rate limit hit (429) - showing cached or placeholder data. Resets in a few minutes'
  if (status === 500) return '500 Server Error - LoopNet API returned an internal error'
  if (status === 503) return '503 Service Unavailable - LoopNet API is temporarily down'
  return body ? `HTTP ${status} from LoopNet API - ${body}` : `HTTP ${status} from LoopNet API`
}

function makeError(message, httpStatus = null, responseBody = '') {
  return {
    message,
    httpStatus,
    responseBody,
    timestamp: new Date().toISOString(),
  }
}

function parseLocation(input = 'Chicago, IL') {
  const value = String(input || '').trim()
  const stateOnly = value.match(/^[A-Z]{2}$/i)
  if (stateOnly) {
    const state = value.toUpperCase()
    return { city: STATE_DEFAULT_CITY[state] || 'Chicago', state }
  }

  const parts = value.split(',').map(part => part.trim()).filter(Boolean)
  if (parts.length >= 2) return { city: parts[0], state: parts[1].slice(0, 2).toUpperCase() }

  return { city: value || 'Chicago', state: 'IL' }
}

function resolveSearchScope(location = 'Chicago, IL') {
  const value = String(location || '').trim()
  const parsed = parseLocation(value)
  // searchByCity is broken on the current plan (400 for every cityId), so all
  // searches route through searchByState using the parsed state code.
  return { type: 'state', ...parsed }
}

function unwrapData(json) {
  if (Array.isArray(json)) return json
  if (Array.isArray(json?.data)) return json.data
  if (Array.isArray(json?.listings)) return json.listings
  if (Array.isArray(json?.results)) return json.results
  if (json?.data && typeof json.data === 'object') return [json.data]
  return json && typeof json === 'object' ? [json] : []
}

function numberFrom(...values) {
  for (const value of values) {
    if (value == null || value === '') continue
    if (typeof value === 'number' && Number.isFinite(value)) return value
    const cleaned = String(value).replace(/[$,%]/g, '').replace(/,/g, '').trim()
    const suffixMatch = cleaned.match(/^([\d.]+)\s*([kmb])$/i)
    if (suffixMatch) {
      const base = Number(suffixMatch[1])
      const multiplier = suffixMatch[2].toLowerCase() === 'k' ? 1_000 : suffixMatch[2].toLowerCase() === 'm' ? 1_000_000 : 1_000_000_000
      if (Number.isFinite(base)) return base * multiplier
    }
    const n = Number(cleaned)
    if (Number.isFinite(n)) return n
  }
  return null
}

function stringFrom(...values) {
  for (const value of values) {
    if (Array.isArray(value)) {
      const text = value.filter(Boolean).join(', ').trim()
      if (text) return text
    }
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

function firstObject(...values) {
  return values.find(value => value && typeof value === 'object' && !Array.isArray(value)) || {}
}

function absolutePath(path) {
  return path.startsWith('/') ? path : `/${path}`
}

async function postJson(path, body, cacheKey, stateType = 'search') {
  if (DIRECT_MODE && !LOOPNET_KEY) {
    const error = makeError('LoopNet API not working - showing placeholder data. Reason: VITE_LOOPNET_API_KEY is not configured')
    setApiState({ type: stateType, ok: false, error, status: 'error' })
    return { ok: false, error, data: null, fromCache: false }
  }

  const cached = cacheGet(CACHE, cacheKey)
  if (cached) return { ok: true, data: cached, error: null, fromCache: true }
  const persisted = persistedGet(cacheKey)
  if (persisted) {
    cacheSet(CACHE, cacheKey, persisted)
    return { ok: true, data: persisted, error: null, fromCache: true }
  }

  const cachedError = cacheGet(ERROR_CACHE, cacheKey)
  if (cachedError) {
    setApiState({
      type: stateType,
      ok: false,
      error: cachedError,
      status: cachedError.httpStatus === 429 ? 'rate-limit' : 'error',
    })
    return { ok: false, error: cachedError, data: null, fromCache: true }
  }

  return dedupe(cacheKey, async () => {
    try {
      const response = await fetch(`${LOOPNET_BASE}${path}`, {
        method: 'POST',
        headers: loopnetHeaders(),
        body: JSON.stringify(body),
      })

      if (!response.ok) {
        const responseBody = await response.text().catch(() => '')
        const error = makeError(httpErrorMessage(response.status, responseBody.slice(0, 180)), response.status, responseBody.slice(0, 500))
        cacheSet(ERROR_CACHE, cacheKey, error)
        setApiState({
          type: stateType,
          ok: false,
          error,
          status: response.status === 429 ? 'rate-limit' : 'error',
        })
        return { ok: false, error, data: null, fromCache: false }
      }

      const json = await response.json()
      ERROR_CACHE.delete(cacheKey)
      cacheSet(CACHE, cacheKey, json)
      persistedSet(cacheKey, json)
      setApiState({ type: stateType, ok: true, error: null, status: 'live', message: 'LoopNet API connected - showing live data' })
      return { ok: true, data: json, error: null, fromCache: false }
    } catch (err) {
      const error = makeError(`LoopNet API not working - showing placeholder data. Reason: Network error - ${err.message}`)
      cacheSet(ERROR_CACHE, cacheKey, error)
      setApiState({ type: stateType, ok: false, error, status: 'error' })
      return { ok: false, error, data: null, fromCache: false }
    }
  })
}

function mapSearchItem(item) {
  return {
    id: item.listingId,
    listingId: item.listingId,
    lat: item.coordinations?.[0]?.[1],
    lng: item.coordinations?.[0]?.[0],
    rawSearch: item,
  }
}

function mapDetailResponse(json) {
  const detail = firstObject(unwrapData(json)[0], json?.property, json?.listing, json?.details, json)
  return detail
}

function parseLocationText(value) {
  const text = String(value || '').trim()
  const match = text.match(/^(.+?),\s*([A-Z]{2})\s*(\d{5})?$/)
  if (!match) return {}
  return { city: match[1], state: match[2], zip: match[3] || '' }
}

function unitsFromText(value) {
  const text = String(value || '')
  return numberFrom(
    text.match(/\b(\d+)\s*(?:unit|units|apartment|apartments)\b/i)?.[1],
    text.match(/\b(\d+)\s*u\b/i)?.[1]
  )
}

function normalizeListing(searchItem, detailJson, listingType) {
  const detail = mapDetailResponse(detailJson)
  const address = firstObject(detail.address, detail.location)
  const facts = firstObject(detail.propertyFacts, detail.property_facts, detail.facts)
  const locationObject = firstObject(detail.location)
  const availableSpace = stringFrom(detail.availableSpace, locationObject.availableSpace)
  const parsedLocation = parseLocationText(detail.location || locationObject.cityState || address.cityState)
  const price = numberFrom(
    detail.price,
    detail.listPrice,
    detail.askingPrice,
    detail.asking_price,
    detail.salePrice,
    detail.sale_price,
    detail.fullPrice,
    detail.monthlyRent,
    detail.leaseRate,
    detail.lease_rate
  )
  const squareFeet = numberFrom(
    detail.squareFeet,
    detail.squareFootage,
    detail.sqft,
    detail.size,
    detail.buildingSize,
    detail.building_size,
    facts.buildingSize,
    facts.building_size,
    facts.grossBuildingArea
  )
  const units = numberFrom(
    detail.units,
    detail.numUnits,
    detail.num_units,
    detail.unitCount,
    detail.unit_count,
    facts.noUnits,
    facts.units,
    unitsFromText(availableSpace),
    unitsFromText(detail.subTitle),
    unitsFromText(detail.subtitle)
  )
  const capRate = numberFrom(detail.capRate, detail.cap_rate, detail.estimatedCapRate, detail.proFormaCapRate)
  const listingId = searchItem.listingId || detail.listingId || detail.id

  return {
    id: listingId,
    listingId,
    lat: searchItem.lat ?? numberFrom(detail.lat, detail.latitude),
    lng: searchItem.lng ?? numberFrom(detail.lng, detail.longitude),
    price,
    pricePerSquareFoot: numberFrom(detail.pricePerSquareFoot, detail.price_per_square_foot),
    propertyType: stringFrom(detail.propertyType, detail.property_type, detail.type, detail.assetType, detail.category, facts.propertyType, availableSpace) || 'Multifamily',
    size: squareFeet,
    squareFeet,
    units,
    capRate,
    title: stringFrom(detail.title, detail.name, detail.propertyName, locationObject.name),
    subTitle: stringFrom(detail.subTitle, detail.subtitle, availableSpace),
    address: stringFrom(detail.address, detail.formattedAddress, detail.fullAddress, address.formattedAddress, address.line1, address.address, locationObject.address),
    street: stringFrom(detail.street, detail.addressLine1, address.street, address.line1, address.address, locationObject.address),
    city: stringFrom(detail.city, address.city, locationObject.city, parsedLocation.city),
    state: stringFrom(detail.state, detail.stateCode, address.state, address.stateCode, locationObject.state, parsedLocation.state),
    zip: stringFrom(detail.zip, detail.zipCode, detail.postalCode, address.zip, address.zipCode, address.postalCode, locationObject.postalCode, parsedLocation.zip),
    status: stringFrom(detail.status) || 'Active',
    yearBuilt: numberFrom(detail.yearBuilt, detail.year_built, facts.yearBuilt),
    daysOnMarket: numberFrom(detail.daysOnMarket, detail.days_on_market, detail.dom),
    listingUrl: stringFrom(detail.listingUrl, detail.listing_url, detail.url),
    listingType,
    details: detail,
    rawListing: {
      ...searchItem.rawSearch,
      ...detail,
    },
  }
}

async function fetchPropertyDetail(listingId, listingType = 'sale') {
  const body = { [DETAIL_BODY_KEY]: String(listingId) }
  const path = listingType === 'lease' ? LEASE_DETAILS_PATH : SALE_DETAILS_PATH
  const cacheKey = `loopnet:detail:${listingType}:${listingId}`
  const result = await postJson(absolutePath(path), body, cacheKey, 'search')
  return {
    detail: result.ok ? result.data : null,
    error: result.error || null,
  }
}

async function fetchBulkDetails(listingIds) {
  if (!listingIds.length) return { details: [], error: null }

  const details = []
  for (let index = 0; index < listingIds.length; index += BULK_DETAILS_BATCH_SIZE) {
    const batch = listingIds.slice(index, index + BULK_DETAILS_BATCH_SIZE).map(String)
    const cacheKey = `loopnet:bulk:${batch.join(',')}`
    const result = await postJson(absolutePath(BULK_DETAILS_PATH), { listingIds: batch }, cacheKey, 'search')
    if (!result.ok) return { details, error: result.error }
    details.push(...unwrapData(result.data))
  }

  return { details, error: null }
}

/**
 * Resolve a two-letter state code to the LoopNet stateId via Helper/findState.
 * Result is cached (memory + localStorage) since state ids never change.
 */
async function resolveStateId(stateCode) {
  const code = String(stateCode || '').toUpperCase()
  const stateName = STATE_NAMES[code] || code
  const cacheKey = `loopnet:findState:${code}`
  const result = await postJson(absolutePath(FIND_STATE_PATH), { keywords: stateName }, cacheKey, 'search')
  if (!result.ok) return { stateId: null, error: result.error }

  const entries = unwrapData(result.data)
  // Prefer an exact state match (geographyType 1 in helper responses),
  // otherwise fall back to the first entry.
  const match =
    entries.find(item => item?.address?.state === code && item?.geographyType === 1) ||
    entries.find(item => item?.geographyType === 1) ||
    entries[0]
  const stateId = match?.id != null ? String(match.id) : null
  if (!stateId) {
    return { stateId: null, error: makeError(`LoopNet Helper/findState returned no id for "${stateName}"`) }
  }
  return { stateId, error: null }
}

/**
 * POST searchByState, discovering the accepted body shape on first use.
 * Tries stateId as string (matches the cityId pattern), then numeric,
 * then state-code shapes. The winning shape name is persisted so later
 * calls cost a single request.
 */
async function searchByStateDiscover({ listingType, stateCode, stateId }) {
  const shapes = [
    { name: 'stateId-string', body: { stateId: String(stateId) } },
    { name: 'stateId-number', body: { stateId: Number(stateId) } },
    { name: 'state-code', body: { state: stateCode } },
    { name: 'stateCode', body: { stateCode } },
  ]

  let preferred = null
  try { preferred = localStorage.getItem(STATE_BODY_SHAPE_KEY) } catch { /* ignore */ }
  const ordered = preferred
    ? [...shapes.filter(s => s.name === preferred), ...shapes.filter(s => s.name !== preferred)]
    : shapes

  const path = `/loopnet/${listingType}/searchByState`
  let lastError = null
  for (const shape of ordered) {
    const cacheKey = `loopnet:search:${listingType}:state:${stateCode}:${shape.name}`
    const result = await postJson(path, shape.body, cacheKey, 'search')
    console.info('[LoopNet] searchByState attempt', shape.name, JSON.stringify(shape.body), result.ok ? 'HTTP OK' : `FAILED ${result.error?.httpStatus ?? 'network'}`)
    if (result.ok) {
      const entries = unwrapData(result.data)
      const isErrorEnvelope = !Array.isArray(result.data) && result.data?.status === 'error'
      if (!isErrorEnvelope && entries.length) {
        try { localStorage.setItem(STATE_BODY_SHAPE_KEY, shape.name) } catch { /* ignore */ }
        return { data: result.data, error: null }
      }
      lastError = makeError(`searchByState (${shape.name}) returned no listings`, null, JSON.stringify(result.data).slice(0, 300))
      continue
    }
    lastError = result.error
    // Anything other than a body-shape rejection (400) is not worth retrying
    // with other shapes - it would just burn quota.
    if (result.error?.httpStatus && result.error.httpStatus !== 400) break
  }
  return { data: null, error: lastError }
}

/**
 * Extract listing ids (and any coordinates) from a v2 search response:
 *   { data: [ { allListingIds: ["37936068", ...], listings: [{listingID, latitude, longitude}] } ] }
 */
function extractV2Search(json) {
  const container = firstObject(unwrapData(json)[0])
  const ids = Array.isArray(container.allListingIds) ? container.allListingIds.map(String) : []
  const listingObjs = Array.isArray(container.listings) ? container.listings : []
  const coordsById = new Map(listingObjs.map(item => [String(item.listingID ?? item.listingId ?? item.id), item]))
  return { ids, coordsById }
}

/**
 * POST /loopnet/v2/{type}/searchByAddress - the only search endpoint the
 * provider currently serves successfully. Tries city+state first, then
 * state-only, then full state name.
 */
async function searchByAddressV2({ listingType, city, state }) {
  const path = `/loopnet/v2/${listingType}/searchByAddress`
  const stateName = STATE_NAMES[String(state).toUpperCase()] || state
  const attempts = [
    { name: 'city+state', body: { country: 'US', state, city, county: null, zipCode: null, page: 1 } },
    { name: 'state-only', body: { country: 'US', state, city: null, county: null, zipCode: null, page: 1 } },
    { name: 'state-name', body: { country: 'US', state: stateName, city: null, county: null, zipCode: null, page: 1 } },
  ]

  let lastError = null
  for (const attempt of attempts) {
    const cacheKey = `loopnet:v2:${listingType}:${attempt.name}:${city}:${state}`
    const result = await postJson(path, attempt.body, cacheKey, 'search')
    console.info('[LoopNet] v2 searchByAddress attempt', attempt.name, JSON.stringify(attempt.body), result.ok ? 'HTTP OK' : `FAILED ${result.error?.httpStatus ?? 'network'}`)
    if (result.ok) {
      const extracted = extractV2Search(result.data)
      if (extracted.ids.length) return { ...extracted, error: null }
      lastError = makeError(`v2 searchByAddress (${attempt.name}) returned no listings`, null, JSON.stringify(result.data).slice(0, 300))
      continue
    }
    lastError = result.error
    if (result.error?.httpStatus && ![400, 422].includes(result.error.httpStatus)) break
  }
  return { ids: [], coordsById: new Map(), error: lastError }
}

async function searchLoopNet({ location = 'Chicago, IL', listingType = 'sale', limit = DEFAULT_SEARCH_LIMIT } = {}) {
  const { city, state } = resolveSearchScope(location)

  let searchListings = null
  let searchError = null

  // Primary: v2 address search (only working search endpoint on this plan).
  const v2 = await searchByAddressV2({ listingType, city, state })
  if (v2.ids.length) {
    searchListings = v2.ids.slice(0, limit).map(id => {
      const coords = firstObject(v2.coordsById.get(id))
      return {
        id,
        listingId: id,
        lat: numberFrom(coords.latitude, coords.lat),
        lng: numberFrom(coords.longitude, coords.lng, coords.lon),
        rawSearch: { listingId: id, ...coords },
      }
    })
  } else {
    searchError = v2.error
  }

  // Legacy v1 fallbacks (currently broken upstream, kept in case the provider
  // fixes them or the plan changes).
  if (!searchListings) {
    let result = null
    const stateResolution = await resolveStateId(state)
    console.info('[LoopNet] resolved state', state, '-> stateId', stateResolution.stateId, stateResolution.error?.message || '')
    if (stateResolution.stateId) {
      const stateSearch = await searchByStateDiscover({ listingType, stateCode: state, stateId: stateResolution.stateId })
      if (stateSearch.data) result = { ok: true, data: stateSearch.data }
    }
    if (!result) {
      const cacheKey = `loopnet:search:${listingType}:city:${city}:${state}:${limit}`
      const cityResult = await postJson(`/loopnet/${listingType}/searchByCity`, { city, state }, cacheKey, 'search')
      if (cityResult.ok) result = cityResult
    }
    if (result?.ok) {
      searchListings = unwrapData(result.data)
        .map(mapSearchItem)
        .filter(item => item.listingId)
        .slice(0, limit)
    }
  }

  if (!searchListings) {
    return { listings: [], error: searchError, httpStatus: searchError?.httpStatus ?? null, isLive: false }
  }

  const bulkResult = await fetchBulkDetails(searchListings.map(item => item.listingId))
  const bulkById = new Map(bulkResult.details.map(detail => [String(detail.listingId), detail]))

  const detailResults = searchListings.map(item => ({
    listing: normalizeListing(item, bulkById.get(String(item.listingId)), listingType),
    error: bulkResult.error,
  }))

  const listings = detailResults.map(result => result.listing)
  const detailErrors = bulkResult.error ? [bulkResult.error] : []
  const allDetailsFailed = searchListings.length > 0 && detailErrors.length === searchListings.length
  if (allDetailsFailed) {
    const error = detailErrors.find(item => item.httpStatus === 429) || detailErrors[0]
    setApiState({
      type: 'search',
      ok: false,
      error,
      status: error.httpStatus === 429 ? 'rate-limit' : 'error',
    })
    return { listings: [], error, httpStatus: error.httpStatus ?? null, isLive: false }
  }

  setApiState({ type: 'search', ok: true, error: null, status: 'live', message: 'LoopNet API connected - showing live data' })
  return { listings, error: null, httpStatus: 200, isLive: true }
}

function average(values) {
  const valid = values.filter(value => Number.isFinite(value) && value > 0)
  if (!valid.length) return null
  return valid.reduce((sum, value) => sum + value, 0) / valid.length
}

function deriveMarketStats(listings, listingType = 'sale') {
  const prices = listings.map(item => numberFrom(item.price))
  const capRates = listings.map(item => numberFrom(item.capRate))
  const pricePerSqft = listings.map(item => {
    const price = numberFrom(item.price)
    const size = numberFrom(item.squareFeet, item.size)
    return price && size ? price / size : null
  })
  const pricePerUnit = listings.map(item => {
    const price = numberFrom(item.price)
    const units = numberFrom(item.units)
    return price && units ? price / units : null
  })

  return {
    avgCapRate: average(capRates),
    avgPricePerUnit: average(pricePerUnit),
    avgPrice: average(prices),
    avgPricePerSqft: average(pricePerSqft),
    totalListings: listings.length,
    listingType,
    isLive: true,
    fetchedAt: new Date().toISOString(),
  }
}

export async function fetchLoopNetListings({
  location = 'Chicago, IL',
  listingType = 'sale',
  limit = DEFAULT_SEARCH_LIMIT,
} = {}) {
  const result = await searchLoopNet({ location, listingType, limit })
  return {
    listings: result.listings,
    error: result.error?.message || null,
    httpStatus: result.httpStatus,
    isLive: result.isLive,
  }
}

export async function fetchLoopNetMarketStats(location = 'Chicago, IL') {
  return fetchLocationMarketStats(location)
}

export async function fetchLoopNetDetail(listingId) {
  const result = await fetchPropertyDetail(listingId, 'sale')
  return result.detail
}

export async function fetchLocationMarketStats(location = 'Chicago, IL') {
  const cacheKey = `loopnet:market:${location}`
  const cached = cacheGet(CACHE, cacheKey)
  if (cached) return cached

  const saleResult = await searchLoopNet({ location, listingType: 'sale', limit: DEFAULT_SEARCH_LIMIT })

  if (saleResult.error) {
    const error = saleResult.error
    setApiState({
      type: 'market',
      ok: false,
      error,
      status: error.httpStatus === 429 ? 'rate-limit' : 'error',
    })
    return null
  }

  const saleStats = deriveMarketStats(saleResult.listings, 'sale')
  const stats = {
    ...saleStats,
    saleListings: saleResult.listings.length,
    leaseListings: null,
    totalListings: saleResult.listings.length,
    avgLeasePrice: null,
    avgLeasePricePerSqft: null,
    isLive: true,
    fetchedAt: new Date().toISOString(),
  }

  setApiState({ type: 'market', ok: true, error: null, status: 'live', message: 'LoopNet API connected - showing live data' })
  return cacheSet(CACHE, cacheKey, stats)
}

export async function fetchPublicStateScores(fallbackStates = {}) {
  return {
    states: fallbackStates,
    sources: {
      population: 'Editorial fallback',
      jobs: 'Editorial fallback',
      rent: 'LoopNet overlay where available; editorial fallback otherwise',
    },
  }
}

export async function fetchRentEstimate(_address) { return null }
export async function fetchMacroPulse() { return null }
export async function fetchStateMarket(_stateCode) { return null }
export async function fetchStateMarketStats(_stateCode) { return null }

export const API_STATUS = {
  // In proxy mode the key lives on the backend, so the frontend is always "configured".
  hasLoopNetKey: DIRECT_MODE ? !!LOOPNET_KEY : true,
}

export function hasAnyApiKey() {
  return API_STATUS.hasLoopNetKey
}
